const METRIC_TOTAL = "stroke-neutral-400 dark:stroke-neutral-500 [stroke-dasharray:4_4]";
const METRIC_GRID = "stroke-neutral-200 dark:stroke-neutral-700";
const METRIC_PLOT = "fill-neutral-50 dark:fill-neutral-900";
const METRIC_EMPTY = "fill-neutral-400 dark:fill-neutral-500 text-xs";
const METRIC_AXIS_TEXT = "fill-neutral-500 dark:fill-neutral-400 text-[11px] tabular-nums";
const METRIC_TOOLTIP =
  "pointer-events-none absolute top-2 z-10 whitespace-nowrap rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-2 py-1 text-xs tabular-nums text-neutral-500 dark:text-neutral-400 shadow-sm";
const METRIC_TOOLTIP_TIME = "text-neutral-900 dark:text-neutral-100";

const LEFT = 0;
const RIGHT = 0;
const TOP = 6;
const AXIS = 18;
const TOOLTIP_OFFSET = 12;

export type Timed = { at: number };

export type Line<T> = { name: string; value: (sample: T) => number | null; line: string; dot: string; swatch: string };

export type Series<T> = {
  lines: Line<T>[];
  max: number;
  total?: boolean;
  step?: boolean;
  height: number;
  describe: (sample: T) => string;
};

type Scale = { x: (at: number) => number; y: (value: number) => number; width: number; start: number; spanMs: number; breakMs: number; live: boolean };

export type ChartWindow = { spanMs: number; stepMs: number; live?: boolean };

const DAY_MS = 24 * 60 * 60 * 1000;

const timeOf = (at: number, spanMs = 0): string =>
  new Date(at).toLocaleString([], { ...(spanMs > DAY_MS ? { weekday: "short" } : {}), hour: "2-digit", minute: "2-digit", hour12: false });

const scaleOf = (width: number, now: number, max: number, height: number, window: ChartWindow): Scale => {
  const start = now - window.spanMs;
  const plot = Math.max(1, width - LEFT - RIGHT);
  const top = max > 0 ? max : 1;
  return {
    x: (at) => LEFT + ((at - start) / window.spanMs) * plot,
    y: (value) => TOP + (1 - value / top) * (height - TOP),
    width,
    start,
    spanMs: window.spanMs,
    breakMs: Math.max(45_000, window.stepMs * 3),
    live: window.live !== false,
  };
};

const pathOf = <T extends Timed>(samples: T[], scale: Scale, line: Line<T>, step: boolean): string => {
  let previous: number | null = null;
  const parts: string[] = [];
  for (const sample of samples) {
    const value = line.value(sample);
    if (value === null) {
      previous = null;
      continue;
    }
    const x = scale.x(sample.at).toFixed(1);
    const y = scale.y(value).toFixed(1);
    const fresh = previous === null || sample.at - previous > scale.breakMs;
    parts.push(fresh ? `M${x},${y}` : step ? `H${x} V${y}` : `L${x},${y}`);
    previous = sample.at;
  }
  return parts.join(" ");
};

const tickValues = (max: number): number[] => {
  if (max > 2) {
    return [0, Math.round(max / 2), max];
  }
  return max < 1 ? [0, max / 2, max] : [0, max];
};

const tickLabel = (tick: number): string => String(Number(tick.toPrecision(3)));

const Axes = <T,>({ scale, series }: { scale: Scale; series: Series<T> }) => {
  const ticks = [...new Set(tickValues(series.max))];
  return (
    <>
      <rect class={METRIC_PLOT} x={LEFT} y={0} width={Math.max(0, scale.width - LEFT - RIGHT)} height={series.height} rx={4} />
      {ticks.map((tick) => (
        <g key={tick}>
          <line
            class={series.total && tick === series.max ? METRIC_TOTAL : METRIC_GRID}
            x1={LEFT}
            x2={scale.width - RIGHT}
            y1={scale.y(tick)}
            y2={scale.y(tick)}
          />
          <text class={METRIC_AXIS_TEXT} x={LEFT + 4} y={tick === 0 ? scale.y(tick) - 3 : scale.y(tick) + 11}>
            {tickLabel(tick)}
          </text>
        </g>
      ))}
      <text class={METRIC_AXIS_TEXT} x={LEFT} y={series.height + 14}>
        {timeOf(scale.start, scale.spanMs)}
      </text>
      <text class={METRIC_AXIS_TEXT} x={scale.width - RIGHT} y={series.height + 14} text-anchor="end">
        {scale.live ? "now" : timeOf(scale.start + scale.spanMs, scale.spanMs)}
      </text>
    </>
  );
};

const nearestAt = <T extends Timed>(samples: T[], scale: Scale, x: number): number | null => {
  let best: T | undefined;
  for (const sample of samples) {
    if (best === undefined || Math.abs(scale.x(sample.at) - x) < Math.abs(scale.x(best.at) - x)) {
      best = sample;
    }
  }
  return best?.at ?? null;
};

const Tooltip = ({ at, x, width, text, spanMs }: { at: number; x: number; width: number; text: string; spanMs: number }) => {
  const style = x > width / 2 ? { right: `${width - x + TOOLTIP_OFFSET}px` } : { left: `${x + TOOLTIP_OFFSET}px` };
  return (
    <div class={METRIC_TOOLTIP} style={style}>
      <div class={METRIC_TOOLTIP_TIME}>{timeOf(at, spanMs)}</div>
      <div>{text}</div>
    </div>
  );
};

type Hover = { hoverAt: number | null; onHover: (at: number | null) => void };

export type SeriesChartProps<T extends Timed> = Hover & {
  samples: T[];
  width: number;
  now: number;
  series: Series<T>;
  label: string;
  window: ChartWindow;
  emptyText: string;
};

export const SeriesChart = <T extends Timed>({ samples: all, width, now, series, label, hoverAt, onHover, window, emptyText }: SeriesChartProps<T>) => {
  const scale = scaleOf(width, now, series.max, series.height, window);
  const samples = all.filter((sample) => sample.at >= scale.start && sample.at <= now);
  const hovered = hoverAt === null ? undefined : samples.find((sample) => sample.at === hoverAt);
  return (
    <div class="relative">
      <svg width={width} height={series.height + AXIS} role="img" aria-label={label}>
        <Axes scale={scale} series={series} />
        {samples.length === 0 && (
          <text class={METRIC_EMPTY} x={LEFT + (width - LEFT - RIGHT) / 2} y={series.height / 2 + 4} text-anchor="middle">
            {emptyText}
          </text>
        )}
        {hovered && <line class={METRIC_GRID} x1={scale.x(hovered.at)} x2={scale.x(hovered.at)} y1={0} y2={series.height} />}
        {series.lines.map((line) => (
          <path key={line.name} class={line.line} d={pathOf(samples, scale, line, series.step === true)} />
        ))}
        {hovered &&
          series.lines.map((line) => {
            const value = line.value(hovered);
            return value === null ? null : <circle key={line.name} class={line.dot} cx={scale.x(hovered.at)} cy={scale.y(value)} r={4} />;
          })}
        <rect
          x={LEFT}
          y={0}
          width={Math.max(0, width - LEFT - RIGHT)}
          height={series.height}
          fill="transparent"
          onPointerMove={(event) => onHover(nearestAt(samples, scale, event.offsetX))}
          onPointerLeave={() => onHover(null)}
        />
      </svg>
      {hovered && <Tooltip at={hovered.at} x={scale.x(hovered.at)} width={width} text={series.describe(hovered)} spanMs={scale.spanMs} />}
    </div>
  );
};
